import React from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';

const UtilityDashboard = () => {
  const navigate = useNavigate();

  // Validate session presence and token expiration
  React.useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (!userStr) {
      navigate("/login");
      return;
    }
    try {
      const userObj = JSON.parse(userStr);
      if (userObj && userObj.exp) {
        const currentTime = Date.now() / 1000;
        if (userObj.exp < currentTime) {
          console.warn("Session expired. Clearing storage and redirecting to login.");
          localStorage.removeItem("user");
          localStorage.removeItem("userMobile");
          navigate("/login");
          return;
        }
      }
    } catch (e) {
      console.error("Invalid user session. Clearing storage and redirecting to login.", e);
      localStorage.removeItem("user");
      localStorage.removeItem("userMobile");
      navigate("/login");
    }
  }, [navigate]);

  // Get current user email from localStorage
  const userStr = localStorage.getItem("user");
  let userEmail = "";
  if (userStr) {
    try {
      const userObj = JSON.parse(userStr);
      userEmail = userObj?.email || "";
    } catch (e) {
      console.error("Failed to parse user from localStorage", e);
    }
  }

  const utilityItems = [
    { name: 'My Contacts', path: '/my-contacts', icon: '👥' },
    { 
      name: 'Mobile Recharge', 
      path: '/mobile-recharge', 
      icon: '📱', 
      disabled: userEmail !== 'contact@cloudai.in' 
    },
    { name: 'Trading Tips', path: '/trading-tips', icon: '📈', disabled: true },
    {
      name: 'TrustApp',
      icon: '🤝',
      children: [
        { name: 'Client', path: '/trustapp/client', icon: '🧑', disabled: true },
        { name: 'Service Provider', path: '/trustapp/service-provider', icon: '🔧', disabled: true },
      ],
    },
    // Added "Pay us" option
    { name: 'Pay us', path: '/pay-us', icon: '💳' },
    { name: 'Customer Support', icon: '🎧', viewKey: 'customer-support' },
  ];

  if (userEmail === 'contact@cloudai.in' || userEmail === 'sonuojha83@gmail.com') {
    utilityItems.push({ name: 'Admin', path: '/trustAdmin-login', icon: '🛡️' });
  }

  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [trustAppOpen, setTrustAppOpen] = React.useState(false);
  const [activeView, setActiveView] = React.useState('home');

  // ── Ticket form state ──
  const [ticketMobile, setTicketMobile] = React.useState('');
  const [ticketSubject, setTicketSubject] = React.useState('');
  const [ticketDetails, setTicketDetails] = React.useState('');
  const [ticketErrors, setTicketErrors] = React.useState({});
  const [ticketSubmitted, setTicketSubmitted] = React.useState(false);
  const [ticketSubmitting, setTicketSubmitting] = React.useState(false);
  const [ticketTopic, setTicketTopic] = React.useState('');

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userMobile");
    navigate('/login');
  };

  // ── Sidebar width ──
  const sidebarWidth = isMobile ? '240px' : '260px';

  return (
    <div style={{
      fontFamily: 'Inter, sans-serif',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    }}>

      {/* ═══ Top Header Bar ═══ */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '12px 16px' : '14px 32px',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        zIndex: 200,
        position: 'sticky',
        top: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Hamburger for mobile */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: 1,
                color: '#333',
              }}
              aria-label="Toggle menu"
            >☰</button>
          )}
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? '1.3rem' : '1.6rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #2196f3, #1565c0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px',
          }}>TrustApp Home</h1>
        </div>

        <button
          onClick={handleLogout}
          style={{
            padding: isMobile ? '6px 14px' : '8px 22px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: isMobile ? '0.8rem' : '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s, transform 0.15s',
            boxShadow: '0 2px 6px rgba(244,67,54,0.3)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#d32f2f';
            e.currentTarget.style.transform = 'scale(1.03)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f44336';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >Logout</button>
      </header>

      {/* ═══ Body: Sidebar + Main Content ═══ */}
      <div style={{ display: 'flex', flex: 1, position: 'relative' }}>

        {/* ── Mobile overlay backdrop ── */}
        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 299,
            }}
          />
        )}

        {/* ── Left Sidebar ── */}
        <nav style={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderRight: '1px solid rgba(0,0,0,0.06)',
          padding: '24px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          boxShadow: '2px 0 16px rgba(0,0,0,0.04)',
          // Mobile: slide-in overlay
          ...(isMobile ? {
            position: 'fixed',
            top: 0,
            left: sidebarOpen ? '0' : `-${sidebarWidth}`,
            height: '100vh',
            zIndex: 300,
            transition: 'left 0.3s ease',
            paddingTop: '70px',
          } : {}),
        }}>
          <div style={{
            padding: '0 20px 16px',
            fontSize: '0.7rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            color: '#9e9e9e',
          }}>Menu</div>

          {utilityItems.map((item) => {
            const isDisabled = item.disabled;
            const hasChildren = item.children && item.children.length > 0;

            // ── Parent item with sub-menu ──
            if (hasChildren) {
              return (
                <div key={item.name}>
                  <div
                    onClick={() => setTrustAppOpen(!trustAppOpen)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '14px 24px',
                      margin: '0 12px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: trustAppOpen ? '#e3f2fd' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e3f2fd';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = trustAppOpen ? '#e3f2fd' : 'transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{item.icon}</span>
                    <span style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: '#333',
                      whiteSpace: 'nowrap',
                    }}>{item.name}</span>
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '0.7rem',
                      transition: 'transform 0.3s ease',
                      transform: trustAppOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      color: '#666',
                    }}>▼</span>
                  </div>

                  {/* Sub-menu items */}
                  <div style={{
                    overflow: 'hidden',
                    maxHeight: trustAppOpen ? `${item.children.length * 52}px` : '0',
                    transition: 'max-height 0.3s ease',
                  }}>
                    {item.children.map((child) => {
                      const childDisabled = child.disabled;
                      return (
                        <div
                          key={child.name}
                          onClick={() => {
                            if (!childDisabled) {
                              if (child.viewKey) {
                                setActiveView(child.viewKey);
                              } else {
                                navigate(child.path);
                              }
                              if (isMobile) setSidebarOpen(false);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '11px 24px 11px 44px',
                            margin: '0 12px',
                            borderRadius: '8px',
                            cursor: childDisabled ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            backgroundColor: 'transparent',
                            opacity: childDisabled ? 0.45 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!childDisabled) {
                              e.currentTarget.style.backgroundColor = '#e8f5e9';
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}
                        >
                          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{child.icon}</span>
                          <span style={{
                            fontSize: '0.88rem',
                            fontWeight: '500',
                            color: childDisabled ? '#aaa' : '#444',
                            whiteSpace: 'nowrap',
                          }}>{child.name}</span>

                          {childDisabled && (
                            <span style={{
                              marginLeft: 'auto',
                              backgroundColor: '#bdbdbd',
                              color: 'white',
                              fontSize: '0.5rem',
                              fontWeight: '700',
                              padding: '2px 6px',
                              borderRadius: '7px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                              flexShrink: 0,
                            }}>Soon</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // ── Regular item ──
            return (
              <div
                key={item.name}
                onClick={() => {
                  if (!isDisabled) {
                    if (item.viewKey) {
                      setActiveView(item.viewKey);
                    } else {
                      navigate(item.path);
                    }
                    if (isMobile) setSidebarOpen(false);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 24px',
                  margin: '0 12px',
                  borderRadius: '10px',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isDisabled ? 0.45 : 1,
                  backgroundColor: 'transparent',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled) {
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{item.icon}</span>
                <span style={{
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: isDisabled ? '#aaa' : '#333',
                  whiteSpace: 'nowrap',
                }}>{item.name}</span>

                {isDisabled && (
                  <span style={{
                    marginLeft: 'auto',
                    backgroundColor: '#bdbdbd',
                    color: 'white',
                    fontSize: '0.55rem',
                    fontWeight: '700',
                    padding: '2px 7px',
                    borderRadius: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    flexShrink: 0,
                  }}>Soon</span>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── Main Content Area ── */}
        <main style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: isMobile ? 'flex-start' : 'center',
          padding: isMobile ? '30px 16px' : '60px 40px',
          overflowY: 'auto',
        }}>
          {activeView === 'home' && (
            <div style={{
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(14px)',
              borderRadius: '20px',
              padding: isMobile ? '36px 24px' : '50px 48px',
              maxWidth: '620px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(255,255,255,0.6)',
            }}>
              <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🔒</div>
              <h2 style={{
                color: '#1565c0',
                fontSize: isMobile ? '1.4rem' : '1.7rem',
                fontWeight: '700',
                marginBottom: '18px',
                marginTop: 0,
              }}>Your Privacy Matters</h2>
              <p style={{
                color: '#546e7a',
                fontSize: '1rem',
                lineHeight: '1.75',
                fontStyle: 'italic',
                fontWeight: '500',
                margin: 0,
              }}>
                Your activities and personal data are completely private to you.
                All information is stored securely in our system in an uniquely encrypted form,
                ensuring your privacy and safety at all times.
              </p>
            </div>
          )}

          {activeView === 'customer-support' && (
            <div style={{
              background: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(14px)',
              borderRadius: '20px',
              padding: isMobile ? '32px 22px' : '44px 44px',
              maxWidth: '520px',
              width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(255,255,255,0.6)',
            }}>
              {ticketSubmitted ? (
                /* ── Success view ── */
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}>✅</div>
                  <h2 style={{
                    color: '#2e7d32',
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    marginTop: 0,
                    marginBottom: '12px',
                  }}>Ticket Submitted!</h2>
                  <p style={{
                    color: '#546e7a',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    marginBottom: '28px',
                  }}>
                    Your support ticket has been submitted successfully.
                    Our team will get back to you shortly.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setTicketMobile('');
                        setTicketSubject('');
                        setTicketDetails('');
                        setTicketTopic('');
                        setTicketErrors({});
                        setTicketSubmitted(false);
                      }}
                      style={{
                        padding: '10px 24px',
                        background: 'linear-gradient(135deg, #2196f3, #1565c0)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'transform 0.15s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >Submit Another</button>
                    <button
                      onClick={() => {
                        setTicketSubmitted(false);
                        setTicketMobile('');
                        setTicketSubject('');
                        setTicketDetails('');
                        setTicketTopic('');
                        setTicketErrors({});
                        setActiveView('home');
                      }}
                      style={{
                        padding: '10px 24px',
                        backgroundColor: 'transparent',
                        color: '#546e7a',
                        border: '1.5px solid #cfd8dc',
                        borderRadius: '10px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#2196f3';
                        e.currentTarget.style.color = '#2196f3';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#cfd8dc';
                        e.currentTarget.style.color = '#546e7a';
                      }}
                    >Back to Home</button>
                  </div>
                </div>
              ) : (
                /* ── Ticket form ── */
                <>
                  <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                    <div style={{ fontSize: '2.8rem', marginBottom: '8px' }}>🎧</div>
                    <h2 style={{
                      margin: 0,
                      fontSize: isMobile ? '1.4rem' : '1.6rem',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #2196f3, #1565c0)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>Customer Support</h2>
                    <p style={{
                      color: '#78909c',
                      fontSize: '0.88rem',
                      marginTop: '6px',
                      marginBottom: 0,
                    }}>Submit a ticket and we'll get back to you</p>
                  </div>

                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const errs = {};
                    if (!/^\d{10}$/.test(ticketMobile)) errs.mobile = 'Please enter a valid 10-digit mobile number';
                    if (!ticketTopic) errs.topic = 'Please select a topic';
                    if (!ticketSubject.trim()) errs.subject = 'Subject is required';
                    else if (ticketSubject.length > 20) errs.subject = 'Max 20 characters';
                    if (!ticketDetails.trim()) errs.details = 'Details are required';
                    else if (ticketDetails.length > 200) errs.details = 'Max 200 characters';
                    setTicketErrors(errs);
                    if (Object.keys(errs).length === 0) {
                      setTicketSubmitting(true);
                      try {
                        const htmlBody = `
                          <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f5f7fa; padding: 0;">
                            <!-- Header -->
                            <div style="background: linear-gradient(135deg, #1565c0, #2196f3); padding: 28px 32px; border-radius: 8px 8px 0 0;">
                              <h2 style="margin: 0; color: #ffffff; font-size: 22px; font-weight: 700;">🎧 Customer Support Ticket</h2>
                              <p style="margin: 6px 0 0; color: rgba(255,255,255,0.85); font-size: 13px;">A new support ticket has been submitted</p>
                            </div>

                            <!-- Body -->
                            <div style="background: #ffffff; padding: 28px 32px; border: 1px solid #e0e0e0; border-top: none;">

                              <!-- Ticket Details Table -->
                              <table style="width: 100%; border-collapse: collapse; margin-bottom: 24px;">
                                <tr>
                                  <td style="padding: 14px 16px; background: #f8f9fa; border: 1px solid #e0e0e0; font-weight: 700; color: #37474f; width: 140px; font-size: 14px;">📱 Mobile</td>
                                  <td style="padding: 14px 16px; border: 1px solid #e0e0e0; color: #212121; font-size: 14px;">${ticketMobile}</td>
                                </tr>
                                <tr>
                                  <td style="padding: 14px 16px; background: #f8f9fa; border: 1px solid #e0e0e0; font-weight: 700; color: #37474f; font-size: 14px;">📝 Subject</td>
                                  <td style="padding: 14px 16px; border: 1px solid #e0e0e0; color: #212121; font-size: 14px;">${ticketSubject}</td>
                                </tr>
                                <tr>
                                  <td style="padding: 14px 16px; background: #f8f9fa; border: 1px solid #e0e0e0; font-weight: 700; color: #37474f; font-size: 14px;">🏷️ Topic</td>
                                  <td style="padding: 14px 16px; border: 1px solid #e0e0e0; color: #212121; font-size: 14px;">${ticketTopic}</td>
                                </tr>
                                <tr>
                                  <td style="padding: 14px 16px; background: #f8f9fa; border: 1px solid #e0e0e0; font-weight: 700; color: #37474f; vertical-align: top; font-size: 14px;">📋 Details</td>
                                  <td style="padding: 14px 16px; border: 1px solid #e0e0e0; color: #212121; font-size: 14px; line-height: 1.6;">${ticketDetails.replace(/\n/g, '<br/>')}</td>
                                </tr>
                              </table>

                              <!-- Quick Actions -->
                              <p style="margin: 0 0 12px; font-size: 13px; font-weight: 600; color: #546e7a; text-transform: uppercase; letter-spacing: 0.5px;">Quick Actions</p>
                              <div>
                                <a href="tel:+91${ticketMobile}" style="display: inline-block; text-decoration: none; background: #43a047; color: #ffffff; padding: 10px 22px; border-radius: 6px; font-size: 14px; font-weight: 600; margin-right: 12px;">📞 Call Customer</a>
                                <a href="https://wa.me/91${ticketMobile}" style="display: inline-block; text-decoration: none; background: #25d366; color: #ffffff; padding: 10px 22px; border-radius: 6px; font-size: 14px; font-weight: 600;">💬 WhatsApp</a>
                              </div>
                            </div>

                            <!-- Footer -->
                            <div style="background: #f8f9fa; padding: 16px 32px; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 8px 8px;">
                              <p style="margin: 0; color: #90a4ae; font-size: 12px;">This is an automated email from TrustApp Support System</p>
                            </div>
                          </div>
                        `;

                        await apiClient.post('/v1/trust/sendEmail', {
                          to: 'contact@cloudai.in',
                          subject: `[Support Ticket] ${ticketSubject}`,
                          phone: ticketMobile,
                          topic: ticketTopic,
                          message: `Mobile: ${ticketMobile}, Topic: ${ticketTopic}, Subject: ${ticketSubject}, Details: ${ticketDetails}`,
                          body: htmlBody,
                          email: userEmail,
                        });

                        setTicketSubmitted(true);
                      } catch (err) {
                        console.error('Email send error:', err);
                        setTicketErrors({ submit: 'Failed to send ticket. Please try again.' });
                      } finally {
                        setTicketSubmitting(false);
                      }
                    }
                  }} noValidate>

                    {/* Mobile Number */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#37474f', marginBottom: '6px' }}>Mobile Number</label>
                      <input
                        type="tel"
                        value={ticketMobile}
                        onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); if (v.length <= 10) setTicketMobile(v); }}
                        placeholder="Enter 10-digit mobile number"
                        inputMode="numeric"
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          fontSize: '0.95rem',
                          border: `1.5px solid ${ticketErrors.mobile ? '#e53935' : '#cfd8dc'}`,
                          borderRadius: '10px',
                          outline: 'none',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          fontFamily: 'Inter, sans-serif',
                          boxSizing: 'border-box',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#2196f3'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(33,150,243,0.12)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = ticketErrors.mobile ? '#e53935' : '#cfd8dc'; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {ticketErrors.mobile ? <span style={{ color: '#e53935', fontSize: '0.78rem', marginTop: '4px', fontWeight: '500' }}>{ticketErrors.mobile}</span> : <span />}
                        <span style={{ textAlign: 'right', fontSize: '0.72rem', color: ticketMobile.length > 10 ? '#e53935' : '#90a4ae', marginTop: '3px', fontWeight: '500' }}>{ticketMobile.length}/10</span>
                      </div>
                    </div>

                    {/* Topic */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#37474f', marginBottom: '10px' }}>Topic</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                        {[
                          { label: 'Rent', emoji: '🏠', color: '#e65100', bg: 'linear-gradient(135deg, #fff3e0, #ffe0b2)', border: '#ff9800', shadow: 'rgba(255,152,0,0.25)' },
                          { label: 'Flat-Buy', emoji: '🏢', color: '#1b5e20', bg: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)', border: '#4caf50', shadow: 'rgba(76,175,80,0.25)' },
                          { label: 'Flat-Sale', emoji: '🏷️', color: '#4a148c', bg: 'linear-gradient(135deg, #f3e5f5, #e1bee7)', border: '#9c27b0', shadow: 'rgba(156,39,176,0.25)' },
                          { label: 'TrustApp queries', emoji: '💬', color: '#0d47a1', bg: 'linear-gradient(135deg, #e3f2fd, #bbdefb)', border: '#2196f3', shadow: 'rgba(33,150,243,0.25)' },
                        ].map(({ label, emoji, color, bg, border, shadow }) => {
                          const isSelected = ticketTopic === label;
                          return (
                            <label
                              key={label}
                              onClick={() => setTicketTopic(label)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 14px',
                                borderRadius: '12px',
                                border: `2px solid ${isSelected ? border : '#e0e0e0'}`,
                                background: isSelected ? bg : '#fafafa',
                                cursor: 'pointer',
                                transition: 'all 0.25s ease',
                                fontSize: '0.88rem',
                                fontWeight: isSelected ? '700' : '500',
                                color: isSelected ? color : '#78909c',
                                userSelect: 'none',
                                boxShadow: isSelected ? `0 4px 14px ${shadow}` : '0 1px 3px rgba(0,0,0,0.04)',
                                transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                              }}
                            >
                              <span style={{
                                fontSize: '1.3rem',
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                background: isSelected ? `${border}22` : '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.25s ease',
                                flexShrink: 0,
                              }}>{emoji}</span>
                              <span style={{ flex: 1 }}>{label}</span>
                              <span style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                border: `2px solid ${isSelected ? border : '#bdbdbd'}`,
                                background: isSelected ? border : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.25s ease',
                                flexShrink: 0,
                              }}>
                                {isSelected && (
                                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {ticketErrors.topic && <span style={{ color: '#e53935', fontSize: '0.78rem', marginTop: '6px', display: 'block', fontWeight: '500' }}>{ticketErrors.topic}</span>}
                    </div>

                    {/* Subject */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#37474f', marginBottom: '6px' }}>Subject</label>
                      <input
                        type="text"
                        value={ticketSubject}
                        onChange={(e) => { if (e.target.value.length <= 20) setTicketSubject(e.target.value); }}
                        placeholder="Brief subject of your issue"
                        maxLength={20}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          fontSize: '0.95rem',
                          border: `1.5px solid ${ticketErrors.subject ? '#e53935' : '#cfd8dc'}`,
                          borderRadius: '10px',
                          outline: 'none',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          fontFamily: 'Inter, sans-serif',
                          boxSizing: 'border-box',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#2196f3'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(33,150,243,0.12)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = ticketErrors.subject ? '#e53935' : '#cfd8dc'; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {ticketErrors.subject ? <span style={{ color: '#e53935', fontSize: '0.78rem', marginTop: '4px', fontWeight: '500' }}>{ticketErrors.subject}</span> : <span />}
                        <span style={{ textAlign: 'right', fontSize: '0.72rem', color: ticketSubject.length > 20 ? '#e53935' : '#90a4ae', marginTop: '3px', fontWeight: '500' }}>{ticketSubject.length}/20</span>
                      </div>
                    </div>

                    {/* Ticket Details */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#37474f', marginBottom: '6px' }}>Ticket Details</label>
                      <textarea
                        value={ticketDetails}
                        onChange={(e) => { if (e.target.value.length <= 200) setTicketDetails(e.target.value); }}
                        placeholder="Describe your issue in detail..."
                        rows={4}
                        maxLength={200}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          fontSize: '0.95rem',
                          border: `1.5px solid ${ticketErrors.details ? '#e53935' : '#cfd8dc'}`,
                          borderRadius: '10px',
                          outline: 'none',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          fontFamily: 'Inter, sans-serif',
                          boxSizing: 'border-box',
                          resize: 'vertical',
                          minHeight: '100px',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#2196f3'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(33,150,243,0.12)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = ticketErrors.details ? '#e53935' : '#cfd8dc'; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {ticketErrors.details ? <span style={{ color: '#e53935', fontSize: '0.78rem', marginTop: '4px', fontWeight: '500' }}>{ticketErrors.details}</span> : <span />}
                        <span style={{ textAlign: 'right', fontSize: '0.72rem', color: ticketDetails.length > 200 ? '#e53935' : '#90a4ae', marginTop: '3px', fontWeight: '500' }}>{ticketDetails.length}/200</span>
                      </div>
                    </div>

                    {/* Buttons */}
                    {ticketErrors.submit && (
                      <p style={{
                        textAlign: 'center',
                        fontSize: '0.82rem',
                        color: '#e53935',
                        marginBottom: '12px',
                        fontWeight: '500',
                      }}>❌ {ticketErrors.submit}</p>
                    )}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        type="submit"
                        disabled={ticketSubmitting}
                        style={{
                          flex: 1,
                          minWidth: '140px',
                          padding: '13px 20px',
                          background: ticketSubmitting ? '#90caf9' : 'linear-gradient(135deg, #2196f3, #1565c0)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          cursor: ticketSubmitting ? 'wait' : 'pointer',
                          transition: 'transform 0.15s, box-shadow 0.2s',
                          boxShadow: ticketSubmitting ? 'none' : '0 4px 14px rgba(33,150,243,0.35)',
                          opacity: ticketSubmitting ? 0.7 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!ticketSubmitting) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(33,150,243,0.45)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!ticketSubmitting) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 14px rgba(33,150,243,0.35)';
                          }
                        }}
                      >{ticketSubmitting ? 'Sending...' : 'Submit Ticket'}</button>
                      <button
                        type="button"
                        onClick={() => {
                          setTicketMobile('');
                          setTicketSubject('');
                          setTicketDetails('');
                          setTicketTopic('');
                          setTicketErrors({});
                          setActiveView('home');
                        }}
                        style={{
                          flex: 1,
                          minWidth: '140px',
                          padding: '13px 20px',
                          backgroundColor: 'transparent',
                          color: '#546e7a',
                          border: '1.5px solid #cfd8dc',
                          borderRadius: '10px',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#2196f3';
                          e.currentTarget.style.color = '#2196f3';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#cfd8dc';
                          e.currentTarget.style.color = '#546e7a';
                        }}
                      >Cancel</button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default UtilityDashboard;